import React, { useEffect, useImperativeHandle, useState ,forwardRef} from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCServicesApi,
	RCCHookToast,
	RCCCheckBox,
	RCCIcon
} from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import Accordion from "src/components/Accordion";
import Loader from "src/components/Loader";
import { ReactComponent as Target } from "src/assets/icons/target.svg";
import { v4 } from "uuid";
import { IndicatorLevel } from "src/components/ModalIndicator/interface";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { IIndicatorGrouper, IIndicatorListCard, IRefIndicatorGrouper } from "../interface";
import  IndicatorListGrouper  from "./listGrouper";

const IndicatorGrouper: React.ForwardRefRenderFunction<IRefIndicatorGrouper,IIndicatorGrouper> = ({
	filters,
	disabled,
	level,
	insertStrategicLevel,
	requestMethod,
	filterHierarchy
},ref) => { 
	
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();

	const [indicatorsList,setIndicatorsList] =  useState<Array<IIndicatorListCard>>([]);
	const [loading, setLoading] = useState(false);
	const [reQuery,setReQuery] = useState(true);
	const [expandedAccordion, setExpandedAccordion] = useState(false);
	const [checkAll,setCheckAll] = useState(false);

	const handleAccordion = (expanded:boolean) => {
		setExpandedAccordion(expanded);

		if(!expanded || !reQuery)
			return;

		executeQuery();
		setReQuery(false);
	};

	const accordionTitle = () => {
		switch (level) 
		{
		case IndicatorLevel.STRATEGIC:
			return (
				<span style={{alignSelf:"center"}}>
					<Target style={{ marginRight: "0.5rem", width: "16px", height: "16px", fill: "var(--purple-lighter)" }} />
					{translate("Estratégico")}
				</span>
			);
		case IndicatorLevel.TATIC:
			return (
				<span style={{alignSelf:"center"}}>
					<RCCIcon name="chessKnight" style={{marginRight: "0.5rem"}}/>
					{translate("Tático")}
				</span>
			);
		default:
			return (
				<span style={{alignSelf:"center"}}>
					<RCCIcon name="tools" style={{marginRight: "0.5rem"}}/>
					{translate("Operacional")}
				</span>
			);
		}
	};

	const executeQuery = async() => {
		setLoading(true);
		try {
			const dataController = await RCCServicesApi.getRequest({
				method: requestMethod,
				urlParameters: {filterHierarchy, level,...filters}
			});
			setIndicatorsList(dataController.data.listRecords);
			setLoading(false);

		} catch (error) {
			standardErrorMessages(error, translate("Erro ao buscar indicadores"), addToast);
			setLoading(false);
		}
	};

	const handleSelectedAll = (setAll:boolean) => setIndicatorsList((prevState) => prevState.map((x) => ({...x,selected:setAll})));

	const forceRequery = () => {
		setExpandedAccordion(false);
		setCheckAll(false);
		setReQuery(true);
	};
	
	const getSelectedIndicators = () => indicatorsList.filter((x) => x.selected)||[];

	const cleanSelectedIndicators = (remove:boolean = true) => {
		if(remove)
			setIndicatorsList((prevState) => prevState.filter((x) => !x.selected));
		else if(checkAll)
			setCheckAll(false);
		else
			setIndicatorsList((prevState) => prevState.map((x) => (x.selected ? {...x,selected:!x.selected} : x)));
	};
	
	const addIndicators = (indicators:Array<IIndicatorListCard>) => 
		setIndicatorsList((prevState) => {
			const newArray = [...prevState,...indicators];
			return newArray.sort(
				(a,b) => {
					if (a.iqc001Codigo > b.iqc001Codigo)
						return 1;
				
					if (a.iqc001Codigo < b.iqc001Codigo) 
						return -1;
				
					return 0;
				});
		});

	useImperativeHandle(ref,() => ({
		getSelectedIndicators,
		cleanSelectedIndicators,
		addIndicators,
		forceRequery
	}));

	useEffect(() => {
		forceRequery();
	}, [filters]);

	useEffect(() => {
		handleSelectedAll(checkAll);
	}, [checkAll]);

	return (
		<Accordion 
			expanded={disabled ? false : expandedAccordion}
			onChange={(_, expanded) => handleAccordion(expanded)}
			TransitionProps={{ unmountOnExit: true }} 
			disabled={disabled}
			header={
				<RCCGrid container 
					spacing={2} 
					wrap="nowrap"
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCCheckBox
							name={`selectAllIndicator-${v4()}`}
							onClick={() => setCheckAll((prevstate) => !prevstate)}
							checked={checkAll}
							disabled={reQuery}
							useStopPropagation
						/>
					</CheckPermission>	
					<RCCGrid container item 
						justifyContent="space-between" 
						alignContent="center"
					>
						{accordionTitle()}
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							{insertStrategicLevel &&
								<RCCTooltip
									text={translate("Inserir na hierarquia indicador de nivel estratégico")}
									color="green"
									position="leftEnd"
								>
									<RCCButton
										icon={{name: "arrowBack", size: 16}}
										outlined
										color="green"
										layout="isRounded"
										size="xs"
										onClick={(e) => { e.stopPropagation(); insertStrategicLevel(null,0);}}
									/>
								</RCCTooltip>
							}													
						</CheckPermission>
					</RCCGrid>
				</RCCGrid>
			}
		>
			{loading ? 
				<Loader/>
				:
				<IndicatorListGrouper 
					indicatorsList={indicatorsList}
					setIndicatorsList={setIndicatorsList}
					hierarchyLevel={level}
				/>
			}
		</Accordion>
	);
};

export default forwardRef(IndicatorGrouper);
